import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, Mountain } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { StoreLink } from "@/components/landing/StoreLinks";
import { Kicker } from "@/components/landing/Section";
import { TrollArt } from "@/components/landing/TrollArt";
import { bySlug } from "@/lib/trolls";
import { STRINGS } from "@/i18n/strings";

const HERO_IMG = "https://images.pexels.com/photos/885597/pexels-photo-885597.jpeg?auto=compress&cs=tinysrgb&w=1600";
const FLOATERS = [
  ["fossekongen", "-left-6 sm:-left-12 -top-8 h-44 sm:h-64"],
  ["nordlysdronninga", "-right-4 sm:-right-10 top-[22%] h-40 sm:h-60"],
  ["vetle-veiviser", "left-[38%] -bottom-10 h-28 sm:h-40"],
].map(([slug, pos]) => [bySlug(slug), pos]);

export const fade = (d) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: d } });

export const Floater = ({ troll, pos, i = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: [0, -6, 0] }}
    transition={{ delay: 0.6 + i * 0.15, y: { repeat: Infinity, duration: 4 + i, ease: "easeInOut" } }}
    className={`absolute pointer-events-none ${pos}`}
    data-testid={`hero-troll-${troll.slug}`}
  >
    <TrollArt troll={troll} className="h-full w-auto drop-shadow-[0_14px_18px_rgba(46,42,37,0.35)]" />
  </motion.div>
);

const HeroPhoto = ({ t }) => (
  <motion.div {...fade(0.25)} className="relative lg:col-span-6">
    <div className="relative rounded-[20px] p-2.5 bg-paper border border-line shadow-journal-lg rotate-[1.2deg]">
      <img
        src={HERO_IMG}
        alt={t.hero.photoNote}
        data-testid="hero-photo"
        className="w-full h-[340px] sm:h-[440px] lg:h-[560px] object-cover rounded-[14px]"
      />
      <div className="absolute right-6 bottom-6 rounded-full bg-parchment/90 backdrop-blur px-3 py-1.5 text-xs font-bold text-ink-muted inline-flex items-center gap-1.5">
        <Mountain className="w-3.5 h-3.5 text-moss" /> {t.hero.photoNote}
      </div>
    </div>
    {FLOATERS.map(([tr, pos], i) => <Floater key={tr.id} troll={tr} pos={pos} i={i} />)}
    <div className="stamp absolute -top-5 right-8 rotate-[8deg]" data-testid="hero-stamp">{t.hero.stamp}</div>
  </motion.div>
);

export default function HeroLive() {
  const { t, lang } = useLang();
  const subLang = lang === "nb" ? "en" : "nb";
  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center" data-testid="hero-slide-fjord">
      <div className="lg:col-span-6 lg:pr-6">
        <motion.div {...fade(0)}><Kicker>{t.hero.kicker}</Kicker></motion.div>
        <motion.h1 {...fade(0.08)} data-testid="hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-ink mt-5">
          {t.hero.title}
        </motion.h1>
        <motion.p {...fade(0.14)} data-testid="hero-subtitle" lang={subLang} className="font-display italic text-lg md:text-xl text-moss mt-4">
          {STRINGS[subLang].hero.title}
        </motion.p>
        <motion.p {...fade(0.2)} className="text-base md:text-lg text-ink-muted leading-relaxed mt-6 max-w-xl">
          {t.hero.pitch}
        </motion.p>
        <motion.div {...fade(0.28)} className="flex flex-wrap items-center gap-3 mt-9">
          <StoreLink
            testId="hero-cta-open-quest"
            className="rounded-full bg-moss hover:bg-moss-dark text-parchment px-7 py-4 text-base font-extrabold shadow-journal transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
          >
            {t.hero.cta}
          </StoreLink>
          <a
            href="#map"
            data-testid="hero-cta-see-map"
            className="rounded-full border-2 border-ink/80 text-ink px-6 py-3.5 text-base font-extrabold inline-flex items-center gap-2 hover:bg-ink hover:text-parchment transition-colors"
          >
            {t.hero.ctaMap} <ArrowDown className="w-4 h-4" />
          </a>
        </motion.div>
        <motion.p {...fade(0.34)} data-testid="hero-tagline" className="mt-6 text-sm font-bold text-rowan">
          {t.tagline}
        </motion.p>
        <motion.dl {...fade(0.4)} className="grid grid-cols-4 gap-4 mt-10 pt-8 border-t border-dashed border-line max-w-lg">
          {t.hero.stats.map(([n, label]) => (
            <div key={label}>
              <dt className="font-display text-3xl font-semibold text-ink tabular-nums">{n}</dt>
              <dd className="text-xs font-bold text-ink-muted mt-1 leading-tight">{label}</dd>
            </div>
          ))}
        </motion.dl>
      </div>
      <HeroPhoto t={t} />
    </div>
  );
}
